/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { defineMessages } from 'react-intl'
import { find, flatMap } from 'lodash'

import type { SliderInstanceType, SliderType, StackDeployment, LinkInfo } from '@modules/ui-types'
import type { ServiceUrl } from '@modules/cloud-api/v1/types'
import { hasDeploymentSolutionViewFeature } from '@modules/deployment-lib'

import { lt } from '@/lib/semver'
import {
  isDeploymentAvailable,
  getFirstAvailableSliderClusterFromGet,
} from '@/lib/stackDeployments/selectors/available'
import { getVersion } from '@/lib/stackDeployments/selectors/fundamentals'
import { getFirstId } from '@/lib/stackDeployments/selectors/clusters'
import { getDeploymentResourceEndpoint } from '@/lib/stackDeployments/selectors/endpoints'
import { getProductSliderTypesForStackDeployment } from '@/lib/sliders/stackDeployments'
import { getSliderPrettyName } from '@/lib/sliders/messages'

import { isFleetServerAvailable } from '../stackDeployments/fleet'

const messages = defineMessages({
  apm: { id: 'applicationLinks.apm', defaultMessage: 'APM' },
  fleet: { id: 'applicationLinks.fleet', defaultMessage: 'Fleet' },
  symbols: { id: 'applicationLinks.symbols', defaultMessage: 'Symbols' },
  profiling: { id: 'applicationLinks.profiling', defaultMessage: 'Profiling' },
  ingest: { id: 'applicationLinks.ingest', defaultMessage: 'Managed OTLP' },
  ingestTechPreview: {
    id: 'applicationLinks.ingest-tech-preview',
    defaultMessage: 'Managed OTLP (technical preview)',
  },
})

export function getLinks({
  deployment,
  show,
  ignoreAlias = false,
  showMotelTechPreviewLabel,
}: {
  deployment: StackDeployment
  show?: SliderInstanceType
  ignoreAlias?: boolean
  showMotelTechPreviewLabel?: boolean
}): LinkInfo[] {
  const deploymentAvailable = isDeploymentAvailable(deployment)
  const showAll = show === undefined
  const version = getVersion({ deployment })

  const sliderInstanceTypes = getProductSliderTypesForStackDeployment(deployment).filter(
    (sliderType) =>
      !excludeSliderType(sliderType) && (showAll || show === sliderType.sliderInstanceType),
  )

  return flatMap(sliderInstanceTypes, makeLinks)

  function makeLinks({ sliderInstanceType, sliderNodeType }: SliderType): LinkInfo[] {
    const availableResource = getFirstAvailableSliderClusterFromGet({
      deployment,
      sliderInstanceType,
    })
    const apiUri = getDeploymentResourceEndpoint({
      deployment,
      sliderInstanceType,
      acceptSsoUrl: false,
      ignoreAlias,
    })
    const uiUri = getDeploymentResourceEndpoint({ deployment, sliderInstanceType, ignoreAlias })
    const label = getSliderPrettyName({ sliderInstanceType, sliderNodeType, version })
    const firstId = sliderInstanceType ? getFirstId({ deployment, sliderInstanceType }) : ``
    const appID = availableResource?.id || firstId || ``

    const showLaunchLink = sliderInstanceType !== `elasticsearch`

    const baseline = {
      appKey: sliderInstanceType,
      appID,
      available: deploymentAvailable && availableResource !== null,
      showLaunchLink:
        sliderInstanceType === 'enterprise_search' && version
          ? lt(version, '8.0.0')
          : showLaunchLink,
    }

    if (
      (sliderInstanceType === `apm` && isFleetServerAvailable({ version })) ||
      sliderInstanceType === `integrations_server`
    ) {
      // UI is just a Kibana deep-link change
      const fleetUiUri = getDeploymentResourceEndpoint({
        deployment,
        sliderInstanceType,
        getDeepLink: () => `/app/fleet#/fleet/agents`,
      })

      // if we are ignoring aliases, we need to synthesise Fleet's URL from APM's.
      const serviceUrls = availableResource?.info.metadata?.services_urls
      const apmApiUri = ignoreAlias ? apiUri : find(serviceUrls, { service: `apm` })?.url
      const fleetApiUri = ignoreAlias
        ? apmApiUri?.replace(`.apm.`, `.fleet.`)
        : find(serviceUrls, { service: `fleet` })?.url

      const apmAndFleetLinks: LinkInfo[] = [
        {
          ...baseline,
          id: `apm-endpoint`,
          label: messages.apm,
          uiUri,
          apiUri: apmApiUri,
          showLaunchLink: !hasDeploymentSolutionViewFeature(deployment),
        },
        {
          ...baseline,
          id: `fleet-endpoint`,
          appKey: `fleet`,
          label: messages.fleet,
          uiUri: fleetUiUri,
          apiUri: fleetApiUri,
        },
      ]

      return [
        ...apmAndFleetLinks,
        ...buildProfilingServiceLinks(baseline, serviceUrls, ignoreAlias, apmApiUri),
      ]
    }

    const defaultAppLink = {
      ...baseline,
      id: `${sliderInstanceType}-endpoint`,
      label,
      uiUri,
      apiUri,
    }

    const serviceUrls = availableResource?.info.metadata?.services_urls
    const hasIngestUrl = serviceUrls?.some((serviceUrl) => serviceUrl.service === `ingest`)

    if (
      sliderInstanceType === `kibana` &&
      hasIngestUrl && // show ingest endpoint only if backend provides the ingest URL (version > 9.0, backend feature flag enabled)
      show === undefined // display ingest endpoint only on main deployment page, not on the Kibana page
    ) {
      const ingestApiUri = ignoreAlias
        ? apiUri?.replace(`.kb.`, `.ingest.`)
        : find(serviceUrls, { service: `ingest` })?.url

      return [
        defaultAppLink,
        {
          ...baseline,
          id: `ingest-endpoint`,
          appKey: `ingest`,
          label: showMotelTechPreviewLabel ? messages.ingestTechPreview : messages.ingest,
          uiUri: ingestApiUri,
          apiUri: ingestApiUri,
          showLaunchLink: false,
          showCopyComponentId: false,
        },
      ]
    }

    return [defaultAppLink]
  }
}

const excludedSliderTypes: SliderType[] = [
  { sliderInstanceType: `elasticsearch`, sliderNodeType: `ml` },
]

function excludeSliderType({ sliderInstanceType, sliderNodeType }: SliderType) {
  return excludedSliderTypes.some(
    (excludedSliderType) =>
      excludedSliderType.sliderInstanceType === sliderInstanceType &&
      excludedSliderType.sliderNodeType === sliderNodeType,
  )
}

// The link is only shown if the service is turned on (on == serviceUrls contains a symbols URL),
// since the symbolization service can be dynamically turned on and off inside integrations-server.
function buildProfilingServiceLinks(
  baseline,
  serviceUrls: ServiceUrl[] | undefined,
  ignoreAlias: boolean,
  apmApiUri: string | undefined,
): LinkInfo[] {
  return ['symbols', 'profiling'].flatMap((service) => {
    const servicesUrl = find(serviceUrls, { service })

    if (servicesUrl === undefined) {
      return []
    }

    const apiUrl = ignoreAlias ? apmApiUri?.replace(`.apm.`, `.${service}.`) : servicesUrl.url

    return [
      {
        ...baseline,
        id: `${service}-endpoint`,
        appKey: service,
        label: messages[service],
        apiUri: apiUrl,
        showLaunchLink: false,
        uiUri: '', // Not shown when showLaunchLink: false
      },
    ]
  })
}
